import { useMutation } from '@apollo/client';
import { Add, Close, EmailOutlined, FlashOnRounded } from '@material-ui/icons';
import { Role, SendInviteEmailsMutation, SendInviteEmailsMutationVariables, User } from 'lib/generated/graphql';
import { SEND_INVITE_EMAILS } from 'lib/mutations';
import { useRef, useState } from 'react';
import { Modal } from 'src/shared-components';
import LoadingOverlay from 'src/shared-components/Loading/LoadingOverlay';
import { useImmer } from 'use-immer';
import { v4 as uuidv4 } from 'uuid';

interface InviteProps {
  referralType: 'FOUNDER' | 'BUILDER';
  user: User;
}

const InviteLinkBox = ({ referralType, user }: InviteProps) => {
  const [loading, setIsLoading] = useState(false);
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [sendInviteEmails] = useMutation<SendInviteEmailsMutation, SendInviteEmailsMutationVariables>(
    SEND_INVITE_EMAILS,
  );
  const emailModalRef = useRef<any>();
  const openEmailModal = () => emailModalRef.current.open();
  const closeEmailModal = () => emailModalRef.current.close();
  const [emails, setEmails] = useImmer({ [uuidv4()]: '' });
  const updateEmail = (key, e) => {
    setEmails(emailsCopy => {
      emailsCopy[key] = e.target.value;
    });
  };

  const sendEmails = async () => {
    const emailValues = Object.values(emails);
    setIsLoading(true);
    await sendInviteEmails({
      variables: { emails: emailValues, referralType: referralType },
    });
    setEmails(() => ({ [uuidv4()]: '' }));
    setTimeout(() => {
      closeEmailModal();
      setIsLoading(false);
    }, 700);
  };

  const addEmail = () => {
    setEmails(emailsCopy => {
      emailsCopy[uuidv4()] = '';
    });
  };

  const removeEmail = key => {
    setEmails(emailsCopy => {
      delete emailsCopy[key];
    });
  };

  const isSendDisabled = () => {
    const values = Object.values(emails);
    if (!values.length) return true;
    else return !values.some(v => v.length > 0);
  };

  const text = {
    builderTitle: 'Invite Builders',
    founderTitleForBuilder: 'Invite Startups to use Huddle',
    founderTitleForFounders: 'Invite fellow founders to use Huddle',
    builerSubTitle: 'Earn $100 when you refer a builder that gets on a project in 90 days.',
    founderSubTitleForBuilders: 'Earn $250 when you refer a founder & they pay for a project.',
    founderSubTitleForFounders: 'Give them expedited access and get $250 when they pay for a project.',
    builderLink: `https://app.huddle.works/builder-invite/${user?.username}`,
    founderLink: `https://app.huddle.works/invite/${user?.username}`,
  };

  const handleCopyClick = () => {
    setIsButtonPressed(true);
    navigator.clipboard.writeText(referralType === 'FOUNDER' ? text.founderLink : text.builderLink).then(() => {
      setTimeout(() => {
        setIsButtonPressed(false);
      }, 200);
    });
  };

  return (
    <div>
      <div
        className={`px-8 py-8 ${user?.role === Role.Builder ? 'mt-8' : ''} bg-white rounded-2xl shadow ${
          user?.role === Role.Builder ? 'max-w-[915px]' : ''
        }`}
      >
        <h2 className="text-2xl sm:text-2xl font-medium">
          {referralType === 'FOUNDER' && user?.role === Role.Customer && text.founderTitleForFounders}
          {referralType === 'FOUNDER' && user?.role !== Role.Customer && text.founderTitleForBuilder}
          {referralType === 'BUILDER' && user?.role === Role.Builder && text.builderTitle}
        </h2>
        <h4 className="text-lg sm:text-[18px] font-medium mt-2 text-[#707070]">
          {referralType === 'FOUNDER' && user?.role === Role.Customer && text.founderSubTitleForFounders}
          {referralType === 'FOUNDER' && user?.role !== Role.Customer && text.founderSubTitleForBuilders}
          {referralType === 'BUILDER' && user?.role === Role.Builder && text.builerSubTitle}
        </h4>
        <div className="font-medium text-sm text-[#838383] mt-10">Your unique invite link</div>
        <div className="flex flex-col sm:flex-row gap-y-4 gap-x-4 mt-4">
          <input
            data-testid="invitecodeinput-input"
            className="grow text-[17px] rounded-2xl appearance-none bg-[#F3F3F3] text-[#00000080] font-normal p-4"
            value={referralType === 'FOUNDER' ? text.founderLink : text.builderLink}
            disabled
          />
          <button
            className={`bg-[#151515] text-white px-10 py-4 rounded-2xl transform ${
              isButtonPressed ? 'scale-95 opacity-80' : ''
            }`}
            onClick={handleCopyClick}
          >
            Copy Link
          </button>
          <button className="border border-black text-black px-10 py-4 rounded-2xl" onClick={() => openEmailModal()}>
            Send Invite
          </button>
        </div>
        {referralType === 'BUILDER' && (
          <div className="mt-10 flex items-center">
            <div className="bg-[#E4B976] rounded-2xl py-3 px-4 text-[#65451D] font-medium uppercase flex">
              <FlashOnRounded className="" />
              <span>{user?.builderInviteCode?.redemptionsLeft} invites available</span>
            </div>
            <div className="text-[#707070] font-medium ml-6">Need more invites? Request more from @Zain on Slack.</div>
          </div>
        )}
      </div>
      <Modal ref={emailModalRef}>
        <div className="p-3 font-medium sm:min-w-[450px]">
          {loading && <LoadingOverlay />}
          <div className="text-[32px]">Send Invite Code</div>
          <div className="mt-1 text[18px] text-[#707070]">Enter emails of people you’d like to refer.</div>
          <div className="mt-6 flex flex-col gap-y-4">
            {Object.keys(emails).map(key => {
              return (
                <div className="flex items-center gap-x-3 relative" key={key}>
                  <input
                    className="border border-[#00000033] rounded-2xl p-5 text-[17px] w-full"
                    onChange={e => updateEmail(key, e)}
                    placeholder="Enter Email..."
                  />

                  <Close
                    className="text-[#00000033] cursor-pointer absolute right-2"
                    onClick={() => removeEmail(key)}
                  />
                </div>
              );
            })}
          </div>
          <button
            onClick={addEmail}
            className="mt-6 bg-[#F3F3F3] text-black text-[17px] rounded-2xl py-6 w-full flex justify-center"
          >
            <span className="mr-4">Add Another Email</span>
            <Add />
          </button>
          <button
            onClick={() => sendEmails()}
            className={`mt-6 bg-black text-white text-[17px] rounded-2xl py-6 w-full flex justify-center ${
              isSendDisabled() && 'opacity-50 cursor-not-allowed'
            }`}
            disabled={isSendDisabled()}
          >
            <span className="mr-4">Send Invite</span>
            <EmailOutlined />
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default InviteLinkBox;
